import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const everydayFaqs = [
  {
    q: "What can I buy from PhantomPath?",
    a: "PhantomPath offers pay-as-you-go VPN passes, travel eSIM data, temporary UK and USA virtual numbers, and selected eSIM + VPN Packs. Available options, prices and durations are shown before payment."
  },
  {
    q: "Do I need a subscription or account?",
    a: "There is no ongoing PhantomPath subscription or automatic renewal. You don't need to create a PhantomPath account to use a VPN pass. Your chosen payment provider may process the information required to complete the transaction, and some telecommunications products may require limited information under their own terms."
  },
  {
    q: "How do I receive and access my purchase?",
    a: "Your access code is displayed after payment is successfully confirmed. Save the access code and any accompanying secret key securely because they are not sent by email."
  },
  {
    q: "Does PhantomPath automatically renew?",
    a: "No. PhantomPath services do not renew automatically. When your purchased service ends, you can choose whether to buy or extend supported access."
  },
  {
    q: "Does my phone support eSIM?",
    a: "eSIM support depends on your phone model, region and mobile network configuration. Check your device manufacturer's compatibility information before buying, and make sure the phone is network-unlocked where required."
  },
  {
    q: "How do I install a travel eSIM?",
    a: "Open your active service in the PhantomPath Portal to view its QR code and installation details, then add the eSIM through your phone's normal cellular settings. Device menu names can vary, so the How It Works page includes the full steps."
  },
  {
    q: "How does the PhantomPath Portal work?",
    a: "Enter the access code shown after payment to view and manage the services included with that purchase. Depending on the product, the Portal can provide a VPN configuration, eSIM details, or supported virtual-number controls."
  },
  {
    q: "Can I call and text with a virtual number?",
    a: "Supported UK and USA virtual-number products provide calling and messaging through the PhantomPath Portal. Availability, included allowances and provider restrictions depend on the selected product. Emergency calling is not supported."
  },
  {
    q: "Will virtual numbers work for verification codes?",
    a: "Some third-party services reject virtual or VoIP numbers, so verification-code compatibility and SMS delivery cannot be guaranteed. Check the requirements of the service you intend to use before buying."
  },
  {
    q: "What happens when my service expires?",
    a: "Access ends when the purchased duration or allowance is exhausted. Nothing renews automatically, and you can purchase or extend supported access if you want to continue."
  },
  {
    q: "What information does PhantomPath retain?",
    a: "PhantomPath does not retain browsing activity, DNS queries, VPN traffic content or customer communications content in its customer database. Limited payment, provisioning, error, security and administrative records may exist where necessary to operate, protect and support the service."
  },
  {
    q: "What does the VPN protect?",
    a: "Traffic routed through an active PhantomPath VPN tunnel is encrypted between your device and the PhantomPath VPN server. This helps protect data in transit on shared networks and normally means websites see the VPN server's IP address instead of your connection's public IP address."
  },
  {
    q: "Which devices and countries are supported?",
    a: "VPN passes work with compatible WireGuard devices. Current travel eSIM destinations are listed on this page, and the available VPN locations are shown in the Portal during active access. Compatibility can depend on your device and operating system version."
  },
  {
    q: "How do refunds work?",
    a: "Refund eligibility depends on whether a digital service has been activated or provisioned and on the circumstances of the purchase. Please read the Refund Policy for the current terms and your statutory rights."
  },
  {
    q: "Who operates PhantomPath?",
    a: "PhantomPath is operated by PhantomPathVPN Ltd, a registered UK company founded by Zack Baker in Swansea, Wales. Company, ICO registration and contact details are published in the website footer and legal pages."
  }
];

const technicalFaqs = [
  {
    q: "What does PhantomPath VPN protect?",
    a: "Traffic routed through an active PhantomPath VPN tunnel is encrypted between your device and the PhantomPath VPN server. This helps protect data in transit on shared networks and normally means websites see the VPN server's IP address instead of your connection's public IP address."
  },
  {
    q: "What devices are supported?",
    a: "PhantomPath VPN passes work with devices that support WireGuard, including compatible Windows, macOS, iOS, Android and Linux devices. Compatibility can depend on your device and operating system version."
  },
  {
    q: "Is there a limit on data usage?",
    a: "VPN passes are sold by access duration. Travel eSIM data allowances are shown on each pack, with separate unlimited data options where available. Fair use, abuse prevention and telecommunications provider terms may apply."
  },
  {
    q: "Can I use it for streaming?",
    a: "Yes. PhantomPath can be used for lawful streaming. Availability, connection performance and regional catalogues depend on the streaming provider, your location and the selected VPN server, so access to a particular service is not guaranteed."
  },
  {
    q: "Which countries can I connect to?",
    a: "The VPN locations currently available to you are shown inside the PhantomPath Portal during active access. Locations may change as routing nodes are added, maintained or temporarily unavailable."
  },
  {
    q: "How secure is the payment process?",
    a: "Payments are processed by Stripe, PayPal or supported UK carrier billing providers, depending on your selection. PhantomPath does not store complete card numbers, security codes or payment credentials. We retain only the limited transaction information needed for provisioning, refunds, fraud prevention, accounting, support and legal obligations."
  },
  {
    q: "Can I get a refund?",
    a: "Unused services may be eligible for a refund within 14 days. Activated digital services generally cannot be refunded after immediate provision has begun. Duplicate payments and technical failures caused by PhantomPath may also qualify. Eligibility remains subject to our Refund Policy and your statutory rights."
  },
  {
    q: "Do I have to provide my phone number?",
    a: "A mobile number is not required to activate a standard VPN pass. Supported USA virtual number products require mobile number verification for telecommunications compliance, fraud prevention and activation. Carrier billing also operates through your mobile network. Verification information is not used for marketing or intentionally linked to VPN traffic, browsing activity, session keys or message content."
  },
  {
    q: "Why does a USA virtual number require a mobile number?",
    a: "Why is this required? US telecom requirements mandate verification to help prevent misuse. Your number is passed to the verification process only for this purpose and is not retained in the PhantomPath customer database. Privacy by design: In line with the data minimisation and purpose limitation principles of UK GDPR Article 5, we only process information necessary to provide and protect the service. Global mobile numbers accepted: This does not need to be a US number. Enter any valid mobile number using its international country code."
  },
  {
    q: "Does a VPN protect me from an IMSI catcher or Stingray?",
    a: "A VPN can protect internet traffic carried inside an active encrypted tunnel, but it does not disable an IMSI catcher or Stingray. Cellular subscriber identifiers, radio network metadata and location related observations operate outside the VPN layer. Ordinary carrier calls and SMS are also not protected by the VPN tunnel."
  },
  {
    q: "How does a PhantomPath virtual number protect my personal number?",
    a: "A virtual number can keep your personal carrier number separate for supported calls and messages. Telecommunications compliance and provider processing still apply. WhatsApp, Meta and other platforms decide which numbers they accept, so verification compatibility and SMS delivery cannot be guaranteed."
  },
  {
    q: "Are virtual number calls and messages stored by PhantomPath?",
    a: "Virtual numbers are supplied through third party telecommunications providers. Message payloads processed by PhantomPath controlled systems are automatically deleted server side within 30 seconds of delivery, and PhantomPath does not retain message history in its customer database. Telecommunications providers may process and retain limited information under their own legal obligations and policies."
  },
  {
    q: "Who operates PhantomPath?",
    a: "PhantomPath is operated by PhantomPathVPN Ltd, a registered UK company founded by Zack. Company, ICO registration and contact details are published through our legal pages and website footer, while Zack's Manifesto explains why the platform was built."
  },
  {
    q: "Is PhantomPath open source?",
    a: "No. PhantomPath is proprietary software operated by PhantomPathVPN Ltd, a registered UK company. Our source code is not publicly available. We publish information about our data handling, VPN architecture and retention practices through our Privacy and Technical Policy and Zero Log Manifesto."
  }
];

export const faqs = [...everydayFaqs, ...technicalFaqs];

const FAQItems = ({ items, prefix }) => items.map((faq, index) => (
  <AccordionItem key={faq.q} value={`${prefix}-${index}`} className="border border-white/10 rounded-lg bg-[#111] px-4">
    <AccordionTrigger className="text-white hover:text-[#3affc2] text-left">
      {faq.q}
    </AccordionTrigger>
    <AccordionContent className="text-gray-400">
      {faq.a}
    </AccordionContent>
  </AccordionItem>
));

const FAQ = ({ standalone = false }) => {
  return (
    <section id="faq" className="py-10 px-4 md:py-12 bg-[#0a0a0a]">
      <div className="container mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          {standalone ? (
            <h1 className="text-2xl md:text-4xl font-bold mb-6 text-white">Frequently Asked Questions</h1>
          ) : (
            <h2 className="text-2xl md:text-4xl font-bold mb-6 text-white">Frequently Asked Questions</h2>
          )}
        </motion.div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          <FAQItems items={everydayFaqs} prefix="everyday" />
        </Accordion>

        {standalone && (
          <>
            <h2 className="mb-6 mt-12 text-xl font-bold text-white md:text-2xl">Privacy and technical questions</h2>
            <Accordion type="single" collapsible className="w-full space-y-4">
              <FAQItems items={technicalFaqs} prefix="technical" />
            </Accordion>
          </>
        )}
      </div>
    </section>
  );
};

export default FAQ;
